import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import useWorldStore from '../store/worldStore';
import {
  CHAT_APP_MODEL_KEYS,
  getChatAppAppearanceModel,
} from '../lib/chatAppCharacters';
import {
  createWorldAgent,
  fetchWorldBootstrap,
  importWorldAgents,
  WorldAgentCreateError,
} from '../../../lib/backend/world';
import { useToastStore } from '../../../lib/toast-store';
import { deriveTrait } from '../lib/deriveTags';
import CharacterHero from './character/CharacterHero';
import PersonaSection from './character/PersonaSection';
import {
  mono,
  labelStyle,
  textInputStyle,
  textAreaStyle,
  sectionHeaderStyle,
  secondaryButtonStyle,
  primaryButtonStyle,
} from './character/tokens';

const AUTONOMY_LEVELS = [
  { id: 0, label: 'Manual', color: '#666', description: 'Only acts when told by you.' },
  { id: 1, label: 'Reactive', color: '#53b1fd', description: 'Completes assigned tasks, uses tools, returns to life sim.' },
  { id: 2, label: 'Proactive', color: '#fbbf24', description: 'Thinks every minute. Self-assigns tasks based on mission.' },
  { id: 3, label: 'Autonomous', color: '#32d583', description: 'Thinks every 15s. Full self-direction with daily summaries.' },
];

const EMPTY_CHARACTER_CONFIG = {
  name: '',
  personality_prompt: '',
  backstory_prompt: '',
  quirks_and_mannerisms: '',
  beliefs_and_intents: '',
  verbosity: 'medium',
};

const CREATION_PHASE_LABELS = {
  preparing_places: 'Preparing place data…',
  generating_identity: 'Generating identity…',
  creating_agent: 'Creating agent…',
  writing_files: 'Writing files…',
  installing_skills: 'Installing skills…',
  syncing_world: 'Syncing world…',
  publishing_character: 'Publishing character…',
};

export default function AgentCreator({ onClose }) {
  const router = useRouter();
  const agents = useWorldStore((s) => s.agents);
  const showToast = useToastStore((s) => s.showToast);
  const updateToast = useToastStore((s) => s.updateToast);

  const [modelKeyIndex, setModelKeyIndex] = useState(() =>
    Math.floor(Math.random() * CHAT_APP_MODEL_KEYS.length),
  );
  const [role, setRole] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [cityCoords, setCityCoords] = useState(null);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const cityDebounceRef = useRef(null);
  const [characterConfig, setCharacterConfig] = useState(EMPTY_CHARACTER_CONFIG);
  const [autonomyLevel, setAutonomyLevel] = useState(0);
  const [mission, setMission] = useState('');
  const [behavioral, setBehavioral] = useState('');
  const [constraints, setConstraints] = useState('');
  const [error, setError] = useState('');
  const [isSettingUp, setIsSettingUp] = useState(false);

  const modelKey = CHAT_APP_MODEL_KEYS[modelKeyIndex];
  const appearance = getChatAppAppearanceModel(modelKey);
  const color = appearance.accent;

  const name = characterConfig.name || '';
  const derivedTrait = useMemo(() => deriveTrait(characterConfig), [characterConfig]);
  const autonomy = AUTONOMY_LEVELS[autonomyLevel];

  useEffect(() => () => clearTimeout(cityDebounceRef.current), []);

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    if (agents.some((agent) => agent.name.toLowerCase() === trimmedName.toLowerCase())) {
      setError('Name already taken');
      return;
    }
    setError('');

    const requestPayload = {
      name: trimmedName,
      role: role.trim() || undefined,
      chat_app_model: modelKey,
      character_config_input: {
        personality_prompt: characterConfig.personality_prompt?.trim() || undefined,
        backstory_prompt: characterConfig.backstory_prompt?.trim() || undefined,
        quirks_and_mannerisms: characterConfig.quirks_and_mannerisms?.trim() || undefined,
        beliefs_and_intents: characterConfig.beliefs_and_intents?.trim() || undefined,
      },
      directives_input: {
        autonomy_level: autonomyLevel,
        mission: mission.trim() || undefined,
        behavioral: behavioral.trim() || undefined,
        constraints: constraints.trim() || undefined,
      },
    };

    if (homeCity.trim()) {
      setIsSettingUp(true);
      try {
        if (cityCoords) {
          const { findPersonalPlaces } = await import('../services/mapboxPlaces.js');
          const bbox =
            cityCoords.bbox || [
              cityCoords.lng - 0.05,
              cityCoords.lat - 0.05,
              cityCoords.lng + 0.05,
              cityCoords.lat + 0.05,
            ];
          const places = await findPersonalPlaces(
            cityCoords,
            { trait: derivedTrait },
            bbox,
          );
          requestPayload.home_city = homeCity.trim();
          requestPayload.home_coords = { lat: cityCoords.lat, lng: cityCoords.lng };
          requestPayload.home_bbox = bbox;
          requestPayload.personal_places = places;
        } else {
          const { setupAgentPlaces } = await import('../services/mapboxPlaces.js');
          const places = await setupAgentPlaces(homeCity.trim(), { trait: derivedTrait });
          requestPayload.home_city = places.homeCity;
          requestPayload.home_coords = places.homeCoords;
          requestPayload.home_bbox = places.homeBbox;
          requestPayload.personal_places = places.personalPlaces;
        }
      } catch (err) {
        console.warn('Place setup failed, using defaults:', err.message);
        requestPayload.home_city = homeCity.trim();
      } finally {
        setIsSettingUp(false);
      }
    }

    const toastId = `world-agent-create-${Date.now()}`;
    let progress = 6;
    let currentPhase = requestPayload.home_city
      ? 'preparing_places'
      : 'generating_identity';
    const progressTimer = setInterval(() => {
      progress +=
        progress < 20 ? 5 : progress < 40 ? 3 : progress < 65 ? 1.75 : progress < 88 ? 0.8 : 0.3;
      progress = Math.min(progress, 94);
      updateToast(toastId, {
        progress,
        message: CREATION_PHASE_LABELS[currentPhase] || 'Creating agent…',
      });
    }, 900);

    const setToastPhase = (phase) => {
      currentPhase = phase;
      updateToast(toastId, {
        progress,
        message: CREATION_PHASE_LABELS[phase] || 'Creating agent…',
      });
    };

    showToast({
      id: toastId,
      message: CREATION_PHASE_LABELS[currentPhase],
      progress,
    });

    onClose();

    try {
      setToastPhase('generating_identity');
      const created = await createWorldAgent(requestPayload);
      clearInterval(progressTimer);

      setToastPhase('syncing_world');
      try {
        await importWorldAgents();
      } catch (syncErr) {
        console.warn('World import refresh failed after create:', syncErr);
      }

      const bootstrap = await fetchWorldBootstrap();
      const store = useWorldStore.getState();
      store.applyBootstrap(bootstrap);
      store.setOpenclawConnected(Boolean(bootstrap?.openclaw_connected));
      store.selectAgent(created.agent_id);

      updateToast(toastId, {
        progress: 100,
        message: `Created "${created.name}" and published it to Characters`,
        action: {
          label: 'Open Characters',
          onClick: () => router.push('/characters'),
        },
      });

      if (Array.isArray(created.warnings) && created.warnings.length > 0) {
        showToast({
          id: `world-agent-create-warning-${created.agent_id}`,
          message: created.warnings.join(' '),
          duration: 7000,
        });
      }
    } catch (err) {
      clearInterval(progressTimer);

      if (err instanceof WorldAgentCreateError) {
        updateToast(toastId, {
          progress: undefined,
          message: `Create failed: ${err.detail || err.message}`,
          variant: 'error',
        });
        if (Array.isArray(err.warnings) && err.warnings.length > 0) {
          showToast({
            id: `world-agent-create-warning-${Date.now()}`,
            message: err.warnings.join(' '),
            duration: 7000,
          });
        }
      } else {
        updateToast(toastId, {
          progress: undefined,
          message: `Create failed: ${err instanceof Error ? err.message : 'Failed to create agent.'}`,
          variant: 'error',
        });
      }
    }
  };

  const heroActions = (
    <div
      style={{
        ...mono,
        fontSize: 9,
        color: 'rgba(255,255,255,0.3)',
        lineHeight: 1.5,
      }}
    >
      Click ◀ ▶ to cycle character models.
    </div>
  );

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 200,
        }}
      />

      <style>{`
        @keyframes creatorIn {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1100,
          maxWidth: '92vw',
          height: 620,
          maxHeight: '88vh',
          background: 'rgba(6,6,7,0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${color}30`,
          boxShadow: 'var(--shadow-glass)',
          borderRadius: 12,
          zIndex: 201,
          overflow: 'hidden',
          animation: 'creatorIn 0.25s cubic-bezier(0.22,1,0.36,1)',
          display: 'flex',
        }}
      >
        {/* Column 1 — Hero */}
        <div style={{ flex: '0 0 150px', display: 'flex', flexDirection: 'column' }}>
          <CharacterHero
            modelKey={modelKey}
            onPrevModel={() =>
              setModelKeyIndex(
                (modelKeyIndex - 1 + CHAT_APP_MODEL_KEYS.length) % CHAT_APP_MODEL_KEYS.length,
              )
            }
            onNextModel={() =>
              setModelKeyIndex((modelKeyIndex + 1) % CHAT_APP_MODEL_KEYS.length)
            }
            name={name || 'New Agent'}
            role={role || 'Agent'}
            trait={derivedTrait}
            color={color}
            actions={heroActions}
          />
        </div>

        {/* Column 2 — Form */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 20px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                ...mono,
                fontSize: 11,
                color: 'rgba(255,255,255,0.55)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
              }}
            >
              New Agent
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                fontSize: 18,
                cursor: 'pointer',
                ...mono,
              }}
            >
              ×
            </button>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 20px',
              display: 'grid',
              gap: 18,
            }}
          >
            {/* Identity */}
            <section style={{ display: 'grid', gap: 10 }}>
              <div style={sectionHeaderStyle}>Identity</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={labelStyle}>Name</span>
                  <input
                    value={name}
                    onChange={(e) => {
                      setCharacterConfig((prev) => ({ ...prev, name: e.target.value }));
                      setError('');
                    }}
                    placeholder="What should we call them?"
                    style={textInputStyle}
                    autoFocus
                  />
                </label>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={labelStyle}>Role</span>
                  <input
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g. Engineer, Writer, Analyst"
                    style={textInputStyle}
                  />
                </label>
              </div>
              <label style={{ display: 'grid', gap: 4, position: 'relative' }}>
                <span style={labelStyle}>Home City</span>
                <input
                  value={homeCity}
                  onChange={(e) => {
                    const val = e.target.value;
                    setHomeCity(val);
                    setCityCoords(null);
                    clearTimeout(cityDebounceRef.current);
                    if (val.trim().length < 2) {
                      setCitySuggestions([]);
                      setShowSuggestions(false);
                      return;
                    }
                    cityDebounceRef.current = setTimeout(async () => {
                      try {
                        const token = process.env.NEXT_PUBLIC_VITE_MAPBOX_TOKEN;
                        const res = await fetch(
                          `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(val)}&limit=5&types=place,locality,neighborhood&access_token=${token}`,
                        );
                        const data = await res.json();
                        const suggestions = (data.features || []).map((f) => ({
                          name: f.properties?.full_address || f.properties?.name || val,
                          lat: f.geometry?.coordinates?.[1],
                          lng: f.geometry?.coordinates?.[0],
                          bbox: f.properties?.bbox,
                        }));
                        setCitySuggestions(suggestions);
                        setShowSuggestions(suggestions.length > 0);
                      } catch {
                        setCitySuggestions([]);
                      }
                    }, 300);
                  }}
                  onFocus={() => {
                    if (citySuggestions.length > 0) setShowSuggestions(true);
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="e.g. Brooklyn, Tokyo, London"
                  style={textInputStyle}
                />
                {showSuggestions && citySuggestions.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'rgba(6,6,7,0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 8,
                      overflow: 'hidden',
                      zIndex: 300,
                      marginTop: 4,
                      boxShadow: 'var(--shadow-elevated)',
                    }}
                  >
                    {citySuggestions.map((s, i) => (
                      <div
                        key={i}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setHomeCity(s.name);
                          setCityCoords({ lat: s.lat, lng: s.lng, bbox: s.bbox });
                          setShowSuggestions(false);
                        }}
                        style={{
                          padding: '6px 10px',
                          cursor: 'pointer',
                          ...mono,
                          fontSize: 10,
                          color: 'rgba(255,255,255,0.7)',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        📍 {s.name}
                      </div>
                    ))}
                  </div>
                )}
              </label>
              {error && (
                <div style={{ ...mono, fontSize: 10, color: '#ef4444' }}>{error}</div>
              )}
            </section>

            {/* Persona */}
            <PersonaSection
              title="Persona"
              mode="edit"
              forceEdit
              showActions={false}
              values={characterConfig}
              onChange={setCharacterConfig}
              color={color}
            />

            {/* Autonomy */}
            <section style={{ display: 'grid', gap: 10 }}>
              <div style={sectionHeaderStyle}>Autonomy & Directives</div>
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.02)',
                  display: 'grid',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ ...labelStyle, marginBottom: 6 }}>Autonomy Level</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="range"
                      min={0}
                      max={3}
                      step={1}
                      value={autonomyLevel}
                      onChange={(e) => setAutonomyLevel(+e.target.value)}
                      style={{ flex: 1, accentColor: autonomy.color }}
                    />
                    <span
                      style={{
                        ...mono,
                        fontSize: 12,
                        fontWeight: 600,
                        color: autonomy.color,
                        minWidth: 90,
                      }}
                    >
                      {autonomy.label}
                    </span>
                  </div>
                  <div
                    style={{
                      ...mono,
                      fontSize: 9,
                      color: 'rgba(255,255,255,0.4)',
                      marginTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {autonomy.description}
                  </div>
                </div>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={labelStyle}>Mission</span>
                  <textarea
                    value={mission}
                    onChange={(e) => setMission(e.target.value)}
                    placeholder="Primary purpose and goals..."
                    rows={2}
                    style={textAreaStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={labelStyle}>Behavioral</span>
                  <textarea
                    value={behavioral}
                    onChange={(e) => setBehavioral(e.target.value)}
                    placeholder="How to approach work..."
                    rows={2}
                    style={textAreaStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 4 }}>
                  <span style={labelStyle}>Constraints</span>
                  <textarea
                    value={constraints}
                    onChange={(e) => setConstraints(e.target.value)}
                    placeholder="What NOT to do..."
                    rows={2}
                    style={textAreaStyle}
                  />
                </label>
              </div>
            </section>
          </div>

          <div
            style={{
              padding: '12px 20px',
              display: 'flex',
              gap: 8,
              flexShrink: 0,
              borderTop: '1px solid rgba(255,255,255,0.06)',
              justifyContent: 'flex-end',
            }}
          >
            <button onClick={onClose} style={{ ...secondaryButtonStyle, minWidth: 90 }}>
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!name.trim() || isSettingUp}
              style={{
                ...primaryButtonStyle,
                minWidth: 140,
                background: name.trim() ? `${color}22` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${name.trim() ? `${color}66` : 'rgba(255,255,255,0.08)'}`,
                color: name.trim() ? `${color}cc` : 'rgba(255,255,255,0.2)',
                opacity: isSettingUp ? 0.6 : 1,
                cursor: !name.trim() ? 'default' : isSettingUp ? 'progress' : 'pointer',
              }}
            >
              {isSettingUp ? 'Preparing…' : 'Create Agent'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
